import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ShieldCheck, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Mock IBKR consent screen. In production this lives on
 * interactivebrokers.com/authorize?oauth_token=... — here we present the same
 * flow inside our own app so the whole loop is testable.
 */
export default function IBKRConsent() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [approving, setApproving] = useState(false);
  const oauth_token = params.get("oauth_token");
  const mode = params.get("mode") || "paper";

  useEffect(() => {
    if (!oauth_token) navigate("/settings");
  }, [oauth_token, navigate]);

  const approve = async () => {
    setApproving(true);
    try {
      const oauth_verifier = "verifier_" + Math.random().toString(36).slice(2, 14);
      const { data } = await api.post("/ibkr/callback", { oauth_token, oauth_verifier });
      toast.success(`IBKR ${data.mode} account ${data.account_id} linked`);
      navigate("/settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Consent failed");
      setApproving(false);
    }
  };

  const deny = () => navigate("/settings");

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="ibkr-consent-page">
      <Card className="max-w-lg w-full border-border rounded-2xl overflow-hidden">
        <div className="bg-[#DC2626] text-white px-6 py-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center font-display font-bold">IB</div>
          <div>
            <div className="font-display font-semibold text-lg leading-none">Interactive Brokers</div>
            <div className="text-xs opacity-80 mt-1">Third-party authorization · mock realm</div>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Authorize</div>
            <h2 className="font-display text-2xl font-bold mt-1">Aanar wants access to your IBKR {mode} account</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Signed in as <span className="font-medium text-foreground">{user?.email}</span>. Aanar
              will be able to read your positions and orders, place & modify orders, and stream
              real-time quotes on your behalf.
            </p>
          </div>

          <div className="rounded-lg border border-border divide-y divide-border">
            {[
              ["Positions & portfolio", "Read"],
              ["Orders (open & recent)", "Read + place / modify / cancel"],
              ["Market data snapshots", "Read"],
              ["Account balance & cash", "Read"],
            ].map(([k, v]) => (
              <div key={k} className="px-4 py-2.5 flex justify-between text-sm">
                <span>{k}</span>
                <span className="text-muted-foreground">{v}</span>
              </div>
            ))}
          </div>

          <div className="text-xs text-muted-foreground flex gap-2">
            <ShieldCheck className="w-4 h-4 shrink-0 text-[hsl(var(--gain))]" />
            <span>OAuth 1.0a · RSA-SHA256 signed handshake · your credentials are never seen by Aanar. You can revoke access at any time.</span>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={deny} className="flex-1 rounded-full" data-testid="ibkr-consent-deny">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={approve} disabled={approving}
                    className="flex-1 rounded-full bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black"
                    data-testid="ibkr-consent-approve">
              {approving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Authorizing</> : <>Authorize</>}
            </Button>
          </div>

          <div className="text-[10px] text-muted-foreground text-center pt-2 border-t border-border">
            MOCK CONSENT · This screen is served by the Aanar backend. In production this URL is
            interactivebrokers.com/authorize.
          </div>
        </div>
      </Card>
    </div>
  );
}
